import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Swords, Trophy } from "lucide-react";

interface SubjectOption {
  id: string;
  name: string;
  displayName: string;
}

interface TopicOption {
  id: string;
  name: string;
  displayName: string;
}

interface ChallengeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opponentId: string;
  opponentName: string;
  userGrade: number;
}

export function ChallengeDialog({
  open,
  onOpenChange,
  opponentId,
  opponentName,
  userGrade
}: ChallengeDialogProps) {
  const { toast } = useToast();
  const [mode, setMode] = useState<'live' | 'async'>('async');
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('all');
  const [questionCount, setQuestionCount] = useState(10);
  const [isCustomCount, setIsCustomCount] = useState(false);
  const [customCount, setCustomCount] = useState('');

  // Fetch subjects for the user's grade
  const { data: subjects = [] } = useQuery<SubjectOption[]>({
    queryKey: ['/api/subjects', userGrade],
    queryFn: async () => {
      const res = await fetch(`/api/subjects/${userGrade}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch subjects');
      return res.json();
    },
    enabled: open && !!userGrade,
  });

  // Fetch topics for the selected subject
  const { data: topics = [] } = useQuery<TopicOption[]>({
    queryKey: ['/api/curriculum/topics', subject],
    queryFn: async () => {
      const subjectEntry = subjects.find(s => s.name === subject);
      if (!subjectEntry) return [];
      const res = await fetch(`/api/curriculum/topics?subjectId=${subjectEntry.id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch topics');
      return res.json();
    },
    enabled: open && !!subject && subjects.length > 0,
  });

  // Auto-select first subject when subjects load
  useEffect(() => {
    if (subjects.length > 0 && !subject) {
      setSubject(subjects[0].name);
    }
  }, [subjects, subject]);

  // Reset topic when subject changes
  useEffect(() => {
    setTopic('all');
  }, [subject]);

  const createChallengeMutation = useMutation({
    mutationFn: async (data: {
      opponentId: string;
      mode: string;
      subject: string;
      topic?: string;
      grade: number;
      questionCount: number;
    }) => {
      const res = await apiRequest('POST', '/api/challenges/create', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/challenges'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversation'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      toast({
        title: "Challenge sent!",
        description: `Your challenge has been sent to ${opponentName}`,
      });
      onOpenChange(false);
      setMode('async');
      setSubject(subjects[0]?.name || '');
      setTopic('all');
      setQuestionCount(10);
    },
    onError: (error: any) => {
      const selectedDisplay = subjects.find(s => s.name === subject)?.displayName || subject;
      let description = error.message || "Please try again";
      if (error.message?.includes("Not enough questions")) {
        description = `There aren't enough ${selectedDisplay} questions available. Try a different subject or fewer questions.`;
      }

      toast({
        title: "Failed to send challenge",
        description,
        variant: "destructive"
      });
    }
  });

  const handleCreateChallenge = () => {
    const finalCount = isCustomCount ? parseInt(customCount) : questionCount;
    if (!finalCount || finalCount < 1 || finalCount > 100) {
      toast({ title: "Invalid question count", description: "Please enter a number between 1 and 100.", variant: "destructive" });
      return;
    }

    // Send the canonical subject name directly: the backend handles mapping
    createChallengeMutation.mutate({
      opponentId,
      mode,
      subject,
      topic: topic === 'all' ? undefined : topic,
      grade: userGrade,
      questionCount: finalCount
    });
  };

  const selectedSubjectDisplay = subjects.find(s => s.name === subject)?.displayName || 'Select subject';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dialog-challenge-create">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Swords className="h-5 w-5" />
            Challenge {opponentName}
          </DialogTitle>
          <DialogDescription>
            Create a quiz challenge and compete with your friend!
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Mode Selection */}
          <div className="space-y-2">
            <Label>Challenge Mode</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as 'live' | 'async')}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="async" id="async" data-testid="radio-mode-async" />
                <Label htmlFor="async" className="font-normal cursor-pointer">
                  <div>
                    <div className="font-medium">Async Mode</div>
                    <div className="text-sm text-muted-foreground">
                      Take the quiz at your own pace. Scores hidden until both finish.
                    </div>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="live" id="live" data-testid="radio-mode-live" />
                <Label htmlFor="live" className="font-normal cursor-pointer">
                  <div>
                    <div className="font-medium">Live Mode</div>
                    <div className="text-sm text-muted-foreground">
                      Real-time race! See each other's progress as you answer.
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Subject Selection */}
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Select value={subject} onValueChange={setSubject}>
              <SelectTrigger id="subject" data-testid="select-subject">
                <SelectValue placeholder="Select subject" />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.displayName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Topic Selection */}
          <div className="space-y-2">
            <Label htmlFor="topic">Topic (Optional)</Label>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger id="topic" data-testid="select-topic">
                <SelectValue placeholder="Any topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any topic</SelectItem>
                {topics.map((t) => (
                  <SelectItem key={t.id} value={t.name}>{t.displayName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Question Count */}
          <div className="space-y-2">
            <Label htmlFor="questionCount">Number of Questions</Label>
            <Select
              value={isCustomCount ? 'custom' : questionCount.toString()}
              onValueChange={(v) => {
                if (v === 'custom') {
                  setIsCustomCount(true);
                  setCustomCount('');
                } else {
                  setIsCustomCount(false);
                  setQuestionCount(parseInt(v));
                }
              }}
            >
              <SelectTrigger id="questionCount" data-testid="select-question-count">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 questions</SelectItem>
                <SelectItem value="20">20 questions</SelectItem>
                <SelectItem value="30">30 questions</SelectItem>
                <SelectItem value="custom">Custom...</SelectItem>
              </SelectContent>
            </Select>
            {isCustomCount && (
              <Input
                type="number"
                min={1}
                max={100}
                placeholder="Enter number of questions (1–100)"
                value={customCount}
                onChange={(e) => setCustomCount(e.target.value)}
                data-testid="input-custom-question-count"
              />
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            data-testid="button-cancel-challenge"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateChallenge}
            disabled={createChallengeMutation.isPending || !subject}
            className="flex-1"
            data-testid="button-send-challenge"
          >
            <Trophy className="h-4 w-4 mr-2" />
            {createChallengeMutation.isPending ? 'Sending...' : 'Send Challenge'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
